import { createContext, useContext, FunctionComponent, ReactNode } from 'react';
import type { TicketApiHooks } from '../api/ticket.api';

/**
 * Context for providing Ticket API hooks to child components
 */
export const TicketApiContext = createContext<TicketApiHooks | null>(null);

/**
 * Provider component that supplies Ticket API hooks to its children via context
 *
 * @example
 * ```tsx
 * <TicketApiProvider hooks={ticketHooks}>
 *   <TicketsContainer />
 * </TicketApiProvider>
 * ```
 */
export type TicketApiProviderProps = {
    hooks: TicketApiHooks;
    children: ReactNode;
};

export const TicketApiProvider: FunctionComponent<TicketApiProviderProps> = ({
    hooks,
    children,
}) => {
    return (
        <TicketApiContext.Provider value={hooks}>
            {children}
        </TicketApiContext.Provider>
    );
};

/**
 * Hook to access Ticket API hooks from context
 *
 * @throws {Error} If used outside of TicketApiProvider
 *
 * @example
 * ```tsx
 * const { useGetTicketsQuery } = useTicketApi();
 * const { data } = useGetTicketsQuery({ sort: 'orderDate,desc' });
 * ```
 */
export const useTicketApi = (): TicketApiHooks => {
    const context = useContext(TicketApiContext);

    if (!context) {
        throw new Error(
            'useTicketApi must be used within a TicketApiProvider. Please wrap your Ticket components with <TicketApiProvider hooks={...}>.'
        );
    }

    return context;
};
